import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../lib/firebase";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function toTime(ts) {
  try {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    return null;
  } catch {
    return null;
  }
}

function isActiveUntil(ts) {
  const ms = msFromTs(ts);
  if (!ms) return false;
  return ms > Date.now();
}

export default function AdminDatingUsersScreen({ goBack, role }) {
  const roleNorm = safe(role).toUpperCase();
  const isAdmin = roleNorm === "ADMIN";

  const [qText, setQText] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;

    setLoading(true);
    setMsg("");

    const ref = collection(db, "datingProfiles");
    const q = query(ref, orderBy("createdAt", "desc"), limit(300));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
        setLoading(false);

        if (selected?.id) {
          const latest = data.find((x) => x.id === selected.id);
          if (latest) setSelected(latest);
        }
      },
      (err) => {
        console.log("datingProfiles listener error:", err);
        setRows([]);
        setLoading(false);
        setMsg(
          "❌ Can't read datingProfiles (rules? missing index? createdAt missing?)"
        );
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const t = safe(qText).toLowerCase();
    if (!t) return rows;

    return rows.filter((u) => {
      const a = safe(u.displayName).toLowerCase();
      const b = safe(u.email).toLowerCase();
      const c = safe(u.username).toLowerCase();
      const d = safe(u.city).toLowerCase();
      const e = safe(u.id).toLowerCase();
      return (
        a.includes(t) || b.includes(t) || c.includes(t) || d.includes(t) || e.includes(t)
      );
    });
  }, [rows, qText]);

  async function patch(uid, data) {
    setMsg("");
    try {
      await updateDoc(doc(db, "datingProfiles", uid), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      setMsg("✅ Updated");
    } catch (e) {
      console.log(e);
      setMsg("❌ Failed (rules?)");
    }
  }

  // -------- actions ----------
  async function banUser(u) {
    const reason = prompt("Ban reason (optional):") || "";
    await patch(u.id, {
      isBanned: true,
      banReason: safe(reason),
      bannedAt: serverTimestamp(),
    });
  }

  async function unbanUser(u) {
    await patch(u.id, {
      isBanned: false,
      banReason: "",
      bannedAt: null,
    });
  }

  async function hideUser(u) {
    const why = prompt("Hide reason (optional):") || "";
    await patch(u.id, { isHidden: true, hiddenReason: safe(why) });
  }

  async function unhideUser(u) {
    await patch(u.id, { isHidden: false, hiddenReason: "" });
  }

  async function verifyUser(u) {
    await patch(u.id, { verified: true, verifiedAt: serverTimestamp() });
  }

  async function unverifyUser(u) {
    await patch(u.id, { verified: false, verifiedAt: null });
  }

  async function disableChat(u) {
    const mins = Number(prompt("Disable chat for how many minutes? (e.g. 60)") || "0");
    if (!Number.isFinite(mins) || mins <= 0) return;
    const untilMs = Date.now() + mins * 60 * 1000;
    await patch(u.id, { chatDisabledUntil: new Date(untilMs) });
  }

  async function enableChat(u) {
    await patch(u.id, { chatDisabledUntil: null });
  }

  async function disableSwipes(u) {
    const mins = Number(
      prompt("Disable swipes for how many minutes? (e.g. 1440 = 1 day)") || "0"
    );
    if (!Number.isFinite(mins) || mins <= 0) return;
    const untilMs = Date.now() + mins * 60 * 1000;
    await patch(u.id, { swipeDisabledUntil: new Date(untilMs) });
  }

  async function enableSwipes(u) {
    await patch(u.id, { swipeDisabledUntil: null });
  }

  async function warnUser(u) {
    const note = prompt("Warning message (saved as lastWarning):") || "";
    await patch(u.id, { lastWarning: safe(note), lastWarningAt: serverTimestamp() });
  }

  async function addStrike(u) {
    const next = Number(u.strikes || 0) + 1;
    await patch(u.id, { strikes: next, lastStrikeAt: serverTimestamp() });

    // optional auto-ban at 3
    if (next >= 3) {
      await patch(u.id, {
        isBanned: true,
        banReason: safe(u.banReason) || "Auto-ban: 3 strikes",
        bannedAt: serverTimestamp(),
      });
    }
  }

  async function resetStrikes(u) {
    const ok = window.confirm("Reset strikes to 0?");
    if (!ok) return;
    await patch(u.id, { strikes: 0 });
  }

  async function resetReports(u) {
    const ok = window.confirm("Reset reportsCount to 0?");
    if (!ok) return;
    await patch(u.id, { reportsCount: 0 });
  }

  if (!isAdmin) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack}>←</button>
          <div>
            <div style={S.h1}>Dating Users</div>
            <div style={S.sub}>Admins only</div>
          </div>
        </div>
        <div style={S.card}>
          Only <b>ADMIN</b> can open Dating moderation.
        </div>
      </div>
    );
  }

  const sel = selected;
  const banned = !!sel?.isBanned;
  const hidden = !!sel?.isHidden;
  const verified = !!sel?.verified;

  const chatOff = isActiveUntil(sel?.chatDisabledUntil);
  const swipesOff = isActiveUntil(sel?.swipeDisabledUntil);

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack}>←</button>
        <div>
          <div style={S.h1}>Dating Moderation</div>
          <div style={S.sub}>Manage users in <code>datingProfiles</code></div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={S.badge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.row}>
          <input
            style={S.input}
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search name / email / username / city / uid..."
          />
          <div style={S.kpi}>{filtered.length} users</div>
        </div>

        {msg ? <div style={S.msg}>{msg}</div> : null}

        <div style={S.grid}>
          <div style={S.list}>
            {loading ? (
              <div style={S.card}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={S.card}>No users in /datingProfiles.</div>
            ) : (
              filtered.map((u) => {
                const isB = !!u.isBanned;
                const isH = !!u.isHidden;
                const isV = !!u.verified;
                return (
                  <button
                    key={u.id}
                    type="button"
                    style={{
                      ...S.userRow,
                      ...(selected?.id === u.id ? S.userRowOn : null),
                    }}
                    onClick={() => setSelected(u)}
                    title={u.id}
                  >
                    <div style={S.userName}>
                      {u.displayName || "No name"}
                      {isB ? <span style={S.pillBad}>BANNED</span> : null}
                      {isH ? <span style={S.pillWarn}>HIDDEN</span> : null}
                      {isV ? <span style={S.pillGood}>VERIFIED</span> : null}
                    </div>
                    <div style={S.userSub}>
                      @{u.username || "—"} • {u.city || "—"} • {u.email || u.id}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div style={S.panel}>
            {!sel ? (
              <div style={S.card}>Pick a user to manage.</div>
            ) : (
              <div>
                <div style={S.panelTitle}>Profile</div>

                <div style={S.pLine}>
                  UID: <span style={S.code}>{sel.id}</span>
                </div>
                <div style={S.pLine}>Email: <b>{sel.email || "—"}</b></div>
                <div style={S.pLine}>Name: <b>{sel.displayName || "—"}</b></div>
                <div style={S.pLine}>Username: <b>@{sel.username || "—"}</b></div>
                <div style={S.pLine}>City: <b>{sel.city || "—"}</b></div>

                <div style={S.pLine}>Created: <b>{toTime(sel.createdAt)}</b></div>
                <div style={S.pLine}>Last active: <b>{toTime(sel.lastActiveAt)}</b></div>

                <div style={S.hr} />

                <div style={S.pLine}>Banned: <b>{banned ? "YES" : "NO"}</b></div>
                <div style={S.pLine}>Ban reason: <b>{sel.banReason || "—"}</b></div>

                <div style={S.pLine}>Hidden: <b>{hidden ? "YES" : "NO"}</b></div>
                <div style={S.pLine}>Verified: <b>{verified ? "YES" : "NO"}</b></div>

                <div style={S.pLine}>
                  Chat restricted: <b>{chatOff ? "YES" : "NO"}</b>{" "}
                  <span style={S.smallNote}>
                    {sel.chatDisabledUntil ? `(until ${toTime(sel.chatDisabledUntil)})` : ""}
                  </span>
                </div>

                <div style={S.pLine}>
                  Swipes restricted: <b>{swipesOff ? "YES" : "NO"}</b>{" "}
                  <span style={S.smallNote}>
                    {sel.swipeDisabledUntil ? `(until ${toTime(sel.swipeDisabledUntil)})` : ""}
                  </span>
                </div>

                <div style={S.pLine}>Strikes: <b>{Number(sel.strikes || 0)}</b></div>
                <div style={S.pLine}>Reports: <b>{Number(sel.reportsCount || 0)}</b></div>

                <div style={S.actions}>
                  {banned ? (
                    <button style={S.btnGood} onClick={() => unbanUser(sel)}>Unban</button>
                  ) : (
                    <button style={S.btnBad} onClick={() => banUser(sel)}>Ban</button>
                  )}

                  {hidden ? (
                    <button style={S.btnGood2} onClick={() => unhideUser(sel)}>Unhide</button>
                  ) : (
                    <button style={S.btnWarn} onClick={() => hideUser(sel)}>Hide</button>
                  )}

                  {verified ? (
                    <button style={S.btnNeutral} onClick={() => unverifyUser(sel)}>Unverify</button>
                  ) : (
                    <button style={S.btnGood2} onClick={() => verifyUser(sel)}>Verify</button>
                  )}
                </div>

                <div style={S.actions}>
                  {chatOff ? (
                    <button style={S.btnNeutral} onClick={() => enableChat(sel)}>Enable chat</button>
                  ) : (
                    <button style={S.btnWarn2} onClick={() => disableChat(sel)}>Disable chat</button>
                  )}

                  {swipesOff ? (
                    <button style={S.btnNeutral} onClick={() => enableSwipes(sel)}>Enable swipes</button>
                  ) : (
                    <button style={S.btnWarn2} onClick={() => disableSwipes(sel)}>Disable swipes</button>
                  )}
                </div>

                <div style={S.actions}>
                  <button style={S.btnWarn2} onClick={() => warnUser(sel)}>Warn</button>
                  <button style={S.btnWarn2} onClick={() => addStrike(sel)}>+ Strike</button>
                  <button style={S.btnNeutral} onClick={() => resetStrikes(sel)}>Reset strikes</button>
                  <button style={S.btnNeutral} onClick={() => resetReports(sel)}>Reset reports</button>
                </div>

                <div style={S.note}>
                  ✅ This manages <code>datingProfiles/{`{uid}`}</code> only.
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={S.note2}>
          Index tip: you’re using <code>orderBy(createdAt)</code> so ensure all datingProfiles have <code>createdAt</code>.
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f7f8ff 0%,#eef2ff 45%,#f8fafc 100%)",
  },
  top: {
    maxWidth: 1100,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },
  wrap: { maxWidth: 1100, margin: "0 auto" },
  row: { display: "flex", gap: 10, alignItems: "center", marginBottom: 12 },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    background: "rgba(255,255,255,0.92)",
  },
  kpi: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontSize: 12,
    fontWeight: 950,
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 900,
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  list: { display: "grid", gap: 10 },
  userRow: {
    textAlign: "left",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.04)",
  },
  userRowOn: {
    border: "1px solid rgba(99,102,241,0.25)",
    background: "rgba(99,102,241,0.08)",
  },
  userName: { display: "flex", gap: 8, alignItems: "center", fontWeight: 950, color: "#0f172a" },
  userSub: { marginTop: 4, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.65)" },

  pillBad: {
    fontSize: 11, fontWeight: 950, padding: "4px 8px", borderRadius: 999,
    border: "1px solid rgba(239,68,68,0.22)", background: "rgba(239,68,68,0.10)",
  },
  pillWarn: {
    fontSize: 11, fontWeight: 950, padding: "4px 8px", borderRadius: 999,
    border: "1px solid rgba(245,158,11,0.22)", background: "rgba(245,158,11,0.10)",
  },
  pillGood: {
    fontSize: 11, fontWeight: 950, padding: "4px 8px", borderRadius: 999,
    border: "1px solid rgba(34,197,94,0.22)", background: "rgba(34,197,94,0.10)",
  },

  panel: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },
  panelTitle: { fontSize: 14, fontWeight: 950, color: "#0f172a", marginBottom: 10 },
  pLine: { fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.72)", marginTop: 8 },
  smallNote: { fontSize: 11, fontWeight: 850, color: "rgba(15,23,42,0.55)" },
  code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  hr: { marginTop: 12, borderTop: "1px solid rgba(15,23,42,0.08)" },

  actions: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },

  btnGood: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(34,197,94,0.20)", background: "rgba(34,197,94,0.12)",
    cursor: "pointer", fontWeight: 950,
  },
  btnGood2: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(59,130,246,0.20)", background: "rgba(59,130,246,0.10)",
    cursor: "pointer", fontWeight: 950,
  },
  btnWarn: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(245,158,11,0.22)", background: "rgba(245,158,11,0.12)",
    cursor: "pointer", fontWeight: 950,
  },
  btnWarn2: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(236,72,153,0.22)", background: "rgba(236,72,153,0.10)",
    cursor: "pointer", fontWeight: 950,
  },
  btnBad: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)", background: "rgba(239,68,68,0.10)",
    cursor: "pointer", fontWeight: 950,
  },
  btnNeutral: {
    padding: "10px 12px", borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)", background: "rgba(255,255,255,0.9)",
    cursor: "pointer", fontWeight: 950,
  },

  card: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
  },
  note: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(248,250,252,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
  note2: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
};